import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const numFields = [
  'bulk_hours', 'tier1_max', 'tier1_rate', 'tier1_rate_bulk',
  'tier2_max', 'tier2_rate', 'tier2_rate_bulk', 'tier3_rate', 'tier3_rate_bulk',
];

const StudioPricingSettings = () => {
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/studio-pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handleField = (key, value) => setPricing((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {};
      numFields.forEach((k) => { payload[k] = Number(pricing[k]); });
      await axios.put(`${API}/studio-pricing`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Studio pricing updated successfully');
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error('Failed to save studio pricing');
    } finally {
      setSaving(false);
    }
  };

  if (!pricing) {
    return <div className="p-8">Loading...</div>;
  }

  const Tier = ({ label, maxKey, rateKey, bulkKey }) => (
    <div className="border border-zinc-200 p-6 space-y-4">
      <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--heading-font)' }}>{label}</h3>
      {maxKey && (
        <div>
          <Label>Max pax for this tier</Label>
          <Input type="number" value={pricing[maxKey]} onChange={(e) => handleField(maxKey, e.target.value)} data-testid={`${maxKey}-input`} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Rate / hour (₱)</Label>
          <Input type="number" value={pricing[rateKey]} onChange={(e) => handleField(rateKey, e.target.value)} data-testid={`${rateKey}-input`} />
        </div>
        <div>
          <Label>Rate / hour ({pricing.bulk_hours}h+)</Label>
          <Input type="number" value={pricing[bulkKey]} onChange={(e) => handleField(bulkKey, e.target.value)} data-testid={`${bulkKey}-input`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8 max-w-3xl" data-testid="studio-pricing-settings">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }} data-testid="studio-pricing-title">
          Studio Pricing
        </h2>
        <p className="text-zinc-600">Set group-size (pax) and duration based rates for studio bookings</p>
      </div>

      <div className="space-y-6">
        <div className="border border-zinc-200 p-6">
          <Label htmlFor="bulk_hours">Discount applies at (hours or more)</Label>
          <Input
            id="bulk_hours"
            type="number"
            className="max-w-[120px]"
            value={pricing.bulk_hours}
            onChange={(e) => handleField('bulk_hours', e.target.value)}
            data-testid="bulk_hours-input"
          />
        </div>

        <Tier label="Tier 1 (smallest group)" maxKey="tier1_max" rateKey="tier1_rate" bulkKey="tier1_rate_bulk" />
        <Tier label="Tier 2 (mid group)" maxKey="tier2_max" rateKey="tier2_rate" bulkKey="tier2_rate_bulk" />
        <Tier label="Tier 3 (largest group, above Tier 2 max)" maxKey={null} rateKey="tier3_rate" bulkKey="tier3_rate_bulk" />

        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-8 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2 disabled:opacity-60"
          data-testid="save-studio-pricing-btn"
        >
          <Save size={20} />
          {saving ? 'SAVING...' : 'SAVE PRICING'}
        </button>
      </div>
    </div>
  );
};

export default StudioPricingSettings;
